import { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import Button from '../components/Button'
import LoadingSpinner from '../components/LoadingSpinner'
import api from '../services/api'
import { getToken, saveToken, saveUserData } from '../utils/userData'

const TOTAL_STEPS = 6

const FacilitatorOnboarding = () => {
  const navigate = useNavigate()
  const location = useLocation()
  
  // Check if we're in edit mode or registration mode
  const isEditMode = location.pathname === '/facilitators/edit-profile'
  const isRegisterMode = location.pathname === '/facilitators/register'
  
  const [loading, setLoading] = useState(true)
  const [verifying, setVerifying] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [submitError, setSubmitError] = useState(null)
  const [currentStep, setCurrentStep] = useState(1)
  const [isSuccess, setIsSuccess] = useState(false)
  const [availableTags, setAvailableTags] = useState([])
  const [searchQuery, setSearchQuery] = useState('')
  const [newTagName, setNewTagName] = useState('')
  const [creatingTag, setCreatingTag] = useState(false)
  
  // Profile picture state
  const [profilePictureFile, setProfilePictureFile] = useState(null)
  const [profilePicturePreview, setProfilePicturePreview] = useState(null)
  const [profilePictureError, setProfilePictureError] = useState(null)
  const fileInputRef = useRef(null)
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    slug: '',
    phoneNumber: '',
    instagramId: '',
    linkedInUrl: '',
    website: '',
    bio: '',
    tags: [],
    phoneVisible: false,
    emailVisible: false,
    instagramVisible: false,
    linkedInVisible: false,
    websiteVisible: true,
  })
  const [formErrors, setFormErrors] = useState({})
  const [slugChecking, setSlugChecking] = useState(false)
  const [slugAvailable, setSlugAvailable] = useState(null)

  // Certification state
  const [certifications, setCertifications] = useState([]) // saved certs (edit mode)
  const [pendingCerts, setPendingCerts] = useState([]) // local certs (register mode)
  const [certFormData, setCertFormData] = useState({ name: '', issuer: '' })
  const [certDocumentFile, setCertDocumentFile] = useState(null)
  const [savingCert, setSavingCert] = useState(false)
  const certFileInputRef = useRef(null)

  useEffect(() => {
    if (isEditMode) {
      // In edit mode, load profile directly
      loadProfileForEdit()
    } else if (isRegisterMode) {
      // In register mode, require login/signup before loading onboarding data
      loadTagsForRegistration()
    } else {
      setError('Invalid route')
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, isRegisterMode])

  // Reset success state when reaching certifications step
  useEffect(() => {
    if (currentStep === 6) {
      setIsSuccess(false)
    }
  }, [currentStep])

  const loadTagsForRegistration = async () => {
    try {
      setVerifying(true)
      setError(null)

      const existingToken = getToken()
      if (!existingToken) {
        navigate(`/login?redirect=${encodeURIComponent('/facilitators/register')}`, {
          replace: true,
        })
        return
      }

      const meResponse = await api.get('/api/auth/me')
      const user = meResponse.data.data

      saveUserData(user)

      if (user.isFacilitator) {
        navigate('/facilitator/dashboard', { replace: true })
        return
      }

      const tagsResponse = await api.get('/api/facilitators/tags')
      const tags = tagsResponse.data.data

      setAvailableTags(tags)
      setFormData((prev) => ({
        ...prev,
        name: user.name || '',
        email: user.email || '',
        phoneNumber: user.phone || '',
      }))
      setVerifying(false)
      setLoading(false)
    } catch (err) {
      console.error('Error loading tags:', err)
      setError(
        err.response?.data?.message ||
          'Failed to load tags. Please try again.'
      )
      setVerifying(false)
      setLoading(false)
    }
  }

  const loadProfileForEdit = async () => {
    try {
      setVerifying(true)
      setError(null)

      // Check if user is logged in
      const existingToken = getToken()
      if (!existingToken) {
        navigate(`/login?redirect=${encodeURIComponent('/facilitators/edit-profile')}`, {
          replace: true,
        })
        return
      }

      // Fetch profile with tags
      const profileResponse = await api.get('/api/facilitators/profile')
      const profile = profileResponse.data.data

      // Fetch available tags
      const tagsResponse = await api.get('/api/facilitators/tags')
      const tags = tagsResponse.data.data

      setAvailableTags(tags)
      setFormData({
        name: profile.name || '',
        email: profile.email || '',
        slug: profile.slug || '',
        phoneNumber: profile.phoneNumber || '',
        instagramId: profile.instagramId || '',
        linkedInUrl: profile.linkedInUrl || '',
        website: profile.website || '',
        bio: profile.bio || '',
        tags: (profile.tags || []).map((tag) => tag.id),
        phoneVisible: profile.phoneVisible || false,
        emailVisible: profile.emailVisible || false,
        instagramVisible: profile.instagramVisible || false,
        linkedInVisible: profile.linkedInVisible || false,
        websiteVisible: profile.websiteVisible !== undefined ? profile.websiteVisible : true,
      })
      // Load existing profile picture
      if (profile.profilePicture) {
        const baseUrl = import.meta.env.VITE_API_URL || ''
        setProfilePicturePreview(`${baseUrl}${profile.profilePicture}`)
      }
      // Mark current slug as available (it's the user's own slug)
      if (profile.slug) {
        setSlugAvailable(true)
      }

      // Fetch certifications
      try {
        const certsResponse = await api.get('/api/facilitators/certifications')
        setCertifications(certsResponse.data.data || [])
      } catch {
        // Non-critical - certifications may not exist yet
      }

      setVerifying(false)
      setLoading(false)
    } catch (err) {
      console.error('Error loading profile:', err)
      setError(
        err.response?.data?.message ||
          'Failed to load profile. Please try again.'
      )
      setLoading(false)
      setVerifying(false)
    }
  }

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }))
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: '',
      }))
    }
    // Reset slug availability when slug changes
    if (name === 'slug') {
      setSlugAvailable(null)
    }
  }

  // Generate slug from name
  const generateSlugFromName = (name) => {
    if (!name) return ''
    return name
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
      .substring(0, 50)
  }

  // Handle slug input with formatting
  const handleSlugChange = (e) => {
    const value = e.target.value
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, '')
      .replace(/-+/g, '-')
      .substring(0, 50)
    
    setFormData((prev) => ({
      ...prev,
      slug: value,
    }))
    setSlugAvailable(null)
    
    if (formErrors.slug) {
      setFormErrors((prev) => ({
        ...prev,
        slug: '',
      }))
    }
  }

  // Check slug availability
  const checkSlugAvailability = async () => {
    if (!formData.slug || formData.slug.length < 3) {
      setFormErrors((prev) => ({
        ...prev,
        slug: 'Profile URL must be at least 3 characters',
      }))
      return
    }

    try {
      setSlugChecking(true)
      const response = await api.get(`/api/facilitators/check-slug/${formData.slug}`)
      const { available, error: slugError } = response.data.data
      
      setSlugAvailable(available)
      if (!available && slugError) {
        setFormErrors((prev) => ({
          ...prev,
          slug: slugError,
        }))
      }
    } catch (err) {
      console.error('Error checking slug:', err)
      setSlugAvailable(false)
      setFormErrors((prev) => ({
        ...prev,
        slug: 'Failed to check availability',
      }))
    } finally {
      setSlugChecking(false)
    }
  }

  // Auto-generate slug from name
  const handleGenerateSlug = () => {
    const generatedSlug = generateSlugFromName(formData.name)
    setFormData((prev) => ({
      ...prev,
      slug: generatedSlug,
    }))
    setSlugAvailable(null)
  }

  const handleTagToggle = (tagId) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.includes(tagId)
        ? prev.tags.filter((id) => id !== tagId)
        : [...prev.tags, tagId],
    }))
  }

  const handleCreateTag = async () => {
    if (!newTagName.trim()) {
      return
    }

    try {
      setCreatingTag(true)
      setError(null)

      const response = await api.post('/api/facilitators/tags', {
        name: newTagName.trim(),
      })

      const newTag = response.data.data

      // Add new tag to available tags
      setAvailableTags((prev) => [...prev, newTag])

      // Add new tag to selected tags
      setFormData((prev) => ({
        ...prev,
        tags: [...prev.tags, newTag.id],
      }))

      // Clear input
      setNewTagName('')
      setCreatingTag(false)
    } catch (err) {
      console.error('Error creating tag:', err)
      setError(
        err.response?.data?.message || 'Failed to create tag. Please try again.'
      )
      setCreatingTag(false)
    }
  }

  // Profile picture handlers
  const handlePictureChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      // Clear previous error
      setProfilePictureError(null)
      
      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        setProfilePictureError('Image size must be less than 5MB')
        // Clear the file input
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
        return
      }
      
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp']
      if (!allowedTypes.includes(file.type)) {
        setProfilePictureError('Please upload a valid image file (JPEG, PNG, GIF, or WebP)')
        // Clear the file input
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
        return
      }
      
      setProfilePictureFile(file)
      
      // Create preview
      const reader = new FileReader()
      reader.onloadend = () => {
        setProfilePicturePreview(reader.result)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleRemovePicture = () => {
    setProfilePictureFile(null)
    setProfilePicturePreview(null)
    setProfilePictureError(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const validateStep2 = () => {
    const errors = {}
    if (!formData.name.trim()) {
      errors.name = 'Name is required'
    }
    if (!formData.email.trim()) {
      errors.email = 'Email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address'
    }
    if (!formData.phoneNumber.trim()) {
      errors.phoneNumber = 'Phone number is required'
    }
    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleNext = () => {
    if (currentStep === 2) {
      if (!validateStep2()) {
        return
      }
    }
    if (currentStep < TOTAL_STEPS) {
      setCurrentStep((prev) => prev + 1)
      setError(null)
      setSubmitError(null)
      setIsSuccess(false) // Reset success state when navigating steps
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1)
      setError(null)
      setSubmitError(null)
      setIsSuccess(false) // Reset success state when navigating steps
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    e.stopPropagation()
    
    // Only allow submission on step 6 and when not already saving/success
    if (currentStep !== 6 || saving || isSuccess) {
      return
    }

    if (isEditMode) {
      // Edit mode: update profile (with optional profile picture)
      try {
        setSaving(true)
        setError(null)
        setSubmitError(null)
        setIsSuccess(false)

        // Use FormData if there's a new profile picture, otherwise use JSON
        if (profilePictureFile) {
          const submitData = new FormData()
          submitData.append('name', formData.name)
          submitData.append('email', formData.email)
          if (formData.slug) submitData.append('slug', formData.slug)
          submitData.append('phoneNumber', formData.phoneNumber || '')
          submitData.append('instagramId', formData.instagramId || '')
          submitData.append('linkedInUrl', formData.linkedInUrl || '')
          submitData.append('website', formData.website || '')
          submitData.append('bio', formData.bio || '')
          submitData.append('tags', JSON.stringify(formData.tags))
          submitData.append('phoneVisible', formData.phoneVisible)
          submitData.append('emailVisible', formData.emailVisible)
          submitData.append('instagramVisible', formData.instagramVisible)
          submitData.append('linkedInVisible', formData.linkedInVisible)
          submitData.append('websiteVisible', formData.websiteVisible)
          submitData.append('profilePicture', profilePictureFile)

          await api.put('/api/facilitators/profile', submitData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          })
        } else {
          await api.put('/api/facilitators/profile', {
            name: formData.name,
            email: formData.email,
            slug: formData.slug || undefined,
            phoneNumber: formData.phoneNumber || null,
            instagramId: formData.instagramId || null,
            linkedInUrl: formData.linkedInUrl || null,
            website: formData.website || null,
            bio: formData.bio || null,
            tags: formData.tags,
            phoneVisible: formData.phoneVisible,
            emailVisible: formData.emailVisible,
            instagramVisible: formData.instagramVisible,
            linkedInVisible: formData.linkedInVisible,
            websiteVisible: formData.websiteVisible,
          })
        }

        setSaving(false)
        setIsSuccess(true)
        setError(null)
        // Reset the file input after successful save
        setProfilePictureFile(null)
        window.scrollTo({ top: 0, behavior: 'smooth' })
      } catch (err) {
        console.error('Error updating profile:', err)
        setError(
          err.response?.data?.message || 'Failed to update profile. Please try again.'
        )
        setSaving(false)
        setIsSuccess(false)
      }
    } else if (isRegisterMode) {
      try {
        setSaving(true)
        setError(null)
        setSubmitError(null)
        setIsSuccess(false)

        const submitData = new FormData()
        submitData.append('name', formData.name)
        submitData.append('phoneNumber', formData.phoneNumber || '')
        submitData.append('instagramId', formData.instagramId || '')
        submitData.append('linkedInUrl', formData.linkedInUrl || '')
        submitData.append('website', formData.website || '')
        submitData.append('bio', formData.bio || '')
        submitData.append('tags', JSON.stringify(formData.tags))
        submitData.append('phoneVisible', formData.phoneVisible)
        submitData.append('emailVisible', formData.emailVisible)
        submitData.append('instagramVisible', formData.instagramVisible)
        submitData.append('linkedInVisible', formData.linkedInVisible)
        submitData.append('websiteVisible', formData.websiteVisible)

        if (profilePictureFile) {
          submitData.append('profilePicture', profilePictureFile)
        }

        // Include pending certifications
        if (pendingCerts.length > 0) {
          submitData.append('certifications', JSON.stringify(
            pendingCerts.map((c) => ({ name: c.name, issuer: c.issuer }))
          ))
          pendingCerts.forEach((cert) => {
            if (cert.file) {
              submitData.append('certDocuments', cert.file)
            }
          })
        }

        const response = await api.post('/api/facilitators/register/complete', submitData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })

        const { token } = response.data.data
        saveToken(token)
        const meResponse = await api.get('/api/auth/me')
        saveUserData(meResponse.data.data)

        setSaving(false)
        setSubmitError(null)
        setProfilePictureFile(null)
        navigate('/facilitator/dashboard', { replace: true })
      } catch (err) {
        console.error('Error completing registration:', err)
        setSubmitError(
          err.response?.data?.message || 'Failed to complete registration. Please try again.'
        )
        setSaving(false)
        setIsSuccess(false)
      }
    }
  }

  const handleGoToDashboard = () => {
    navigate('/facilitator/dashboard')
  }

  const renderProgressBar = () => {
    const steps = Array.from({ length: TOTAL_STEPS }, (_, i) => i + 1)
    return (
      <div className="mb-6 md:mb-8">
        <div className="flex items-center justify-between mb-2 px-1">
          {steps.map((step) => (
            <div
              key={step}
              className={`flex items-center ${
                step < TOTAL_STEPS ? 'flex-1' : ''
              }`}
            >
              <div
                className={`w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center font-semibold text-sm md:text-base transition-all ${
                  step <= currentStep
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-200 text-gray-500'
                }`}
              >
                {step < currentStep ? '✓' : step}
              </div>
              {step < TOTAL_STEPS && (
                <div
                  className={`flex-1 h-1 mx-1 md:mx-2 transition-all ${
                    step < currentStep ? 'bg-primary-600' : 'bg-gray-200'
                  }`}
                />
              )}
            </div>
          ))}
        </div>
        <div className="text-center text-xs md:text-sm text-gray-600 mt-2">
          Step {currentStep} of {TOTAL_STEPS}
        </div>
      </div>
    )
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <motion.div
            key="step1"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div className="text-center">
              <div className="text-5xl md:text-6xl mb-3 md:mb-4">
                {isEditMode ? '✏️' : '👋'}
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3 md:mb-4">
                {isEditMode ? 'Edit Your Profile' : 'Join Wellness Loop!'}
              </h2>
              <p className="text-base md:text-lg text-gray-600 mb-4 md:mb-6">
                {isEditMode
                  ? 'Update your profile information below.'
                  : 'Create your facilitator profile and join our community.'}
              </p>
            </div>
          </motion.div>
        )

      case 2:
        return (
          <motion.div
            key="step2"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 md:mb-6">
              Basic Information
            </h2>
            {/* <p className="text-gray-600 mb-6">
              Let&apos;s start with your basic contact information.
            </p> */}

            {/* Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                Name *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className={`w-full px-3 sm:px-4 py-2.5 md:py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all text-sm md:text-base ${
                  formErrors.name
                    ? 'border-red-500 bg-red-50'
                    : 'border-gray-200 bg-gray-50'
                }`}
                placeholder="Enter your full name"
              />
              {formErrors.name && (
                <p className="mt-1 text-sm text-red-600">{formErrors.name}</p>
              )}
            </div>

            {/* Email */}
            <div>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email *
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="emailVisible"
                    checked={formData.emailVisible}
                    onChange={handleInputChange}
                    className="w-4 h-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <span className="text-xs sm:text-sm text-gray-600">Show in directory</span>
                </label>
              </div>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={isEditMode ? handleInputChange : undefined}
                readOnly={isRegisterMode}
                className={`w-full px-3 sm:px-4 py-2.5 md:py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all text-sm md:text-base ${
                  formErrors.email
                    ? 'border-red-500 bg-red-50'
                    : isRegisterMode
                    ? 'border-gray-200 bg-gray-100 text-gray-600 cursor-not-allowed'
                    : 'border-gray-200 bg-gray-50'
                }`}
                placeholder="your.email@example.com"
              />
              {formErrors.email && (
                <p className="mt-1 text-sm text-red-600">{formErrors.email}</p>
              )}
            </div>

            {/* Phone Number */}
            <div>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                <label
                  htmlFor="phoneNumber"
                  className="block text-sm font-medium text-gray-700"
                >
                  Phone Number *
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="phoneVisible"
                    checked={formData.phoneVisible}
                    onChange={handleInputChange}
                    className="w-4 h-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <span className="text-xs sm:text-sm text-gray-600">Show in directory</span>
                </label>
              </div>
              <input
                type="tel"
                id="phoneNumber"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleInputChange}
                className={`w-full px-3 sm:px-4 py-2.5 md:py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all text-sm md:text-base ${
                  formErrors.phoneNumber
                    ? 'border-red-500 bg-red-50'
                    : 'border-gray-200 bg-gray-50'
                }`}
                placeholder="+1-555-0100"
              />
              {formErrors.phoneNumber && (
                <p className="mt-1 text-sm text-red-600">{formErrors.phoneNumber}</p>
              )}
            </div>

            {/* Profile URL / Slug - Only show in edit mode */}
            {isEditMode && (
              <div>
                <label htmlFor="slug" className="block text-sm font-medium text-gray-700 mb-2">
                  Profile URL
                </label>
                <div className="flex flex-col gap-2">
                  <div className="flex gap-2">
                    <div className="flex-1 relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
                        /facilitator/
                      </span>
                      <input
                        type="text"
                        id="slug"
                        name="slug"
                        value={formData.slug}
                        onChange={handleSlugChange}
                        className={`w-full pl-24 pr-3 sm:pl-28 sm:pr-4 py-2.5 md:py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all text-sm md:text-base ${
                          formErrors.slug
                            ? 'border-red-500 bg-red-50'
                            : slugAvailable === true
                            ? 'border-green-500 bg-green-50'
                            : slugAvailable === false
                            ? 'border-red-500 bg-red-50'
                            : 'border-gray-200 bg-gray-50'
                        }`}
                        placeholder="your-profile-url"
                      />
                      {slugAvailable === true && (
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-green-600">
                          ✓
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={checkSlugAvailability}
                      disabled={slugChecking || !formData.slug}
                      className="px-3 py-2 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors text-sm whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {slugChecking ? '...' : 'Check'}
                    </button>
                  </div>
                  <div className="flex items-center justify-between">
                    <button
                      type="button"
                      onClick={handleGenerateSlug}
                      className="text-xs text-primary-600 hover:text-primary-700 underline"
                    >
                      Generate from name
                    </button>
                    {slugAvailable === true && (
                      <span className="text-xs text-green-600">Available!</span>
                    )}
                  </div>
                  {formErrors.slug && (
                    <p className="text-sm text-red-600">{formErrors.slug}</p>
                  )}
                  <p className="text-xs text-gray-500">
                    This is your unique profile URL. Use only lowercase letters, numbers, and hyphens.
                  </p>
                </div>
              </div>
            )}
          </motion.div>
        )

      case 3:
        return (
          <motion.div
            key="step3"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 md:mb-6">
              Social Media Links
            </h2>
            {/* <p className="text-gray-600 mb-6">
              Help people connect with you on social media (optional).
            </p> */}

            {/* Instagram ID */}
            <div>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                <label
                  htmlFor="instagramId"
                  className="block text-sm font-medium text-gray-700"
                >
                  Instagram ID
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="instagramVisible"
                    checked={formData.instagramVisible}
                    onChange={handleInputChange}
                    className="w-4 h-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <span className="text-xs sm:text-sm text-gray-600">Show in directory</span>
                </label>
              </div>
              <input
                type="text"
                id="instagramId"
                name="instagramId"
                value={formData.instagramId}
                onChange={handleInputChange}
                className="w-full px-3 sm:px-4 py-2.5 md:py-3 border-2 border-gray-200 bg-gray-50 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all text-sm md:text-base"
                placeholder="@yourusername"
              />
            </div>

            {/* LinkedIn URL */}
            <div>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                <label
                  htmlFor="linkedInUrl"
                  className="block text-sm font-medium text-gray-700"
                >
                  LinkedIn URL
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="linkedInVisible"
                    checked={formData.linkedInVisible}
                    onChange={handleInputChange}
                    className="w-4 h-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <span className="text-xs sm:text-sm text-gray-600">Show in directory</span>
                </label>
              </div>
              <input
                type="url"
                id="linkedInUrl"
                name="linkedInUrl"
                value={formData.linkedInUrl}
                onChange={handleInputChange}
                className="w-full px-3 sm:px-4 py-2.5 md:py-3 border-2 border-gray-200 bg-gray-50 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all text-sm md:text-base"
                placeholder="https://linkedin.com/in/yourprofile"
              />
            </div>

            {/* Website */}
            <div>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                <label
                  htmlFor="website"
                  className="block text-sm font-medium text-gray-700"
                >
                  Website
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="websiteVisible"
                    checked={formData.websiteVisible}
                    onChange={handleInputChange}
                    className="w-4 h-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <span className="text-xs sm:text-sm text-gray-600">Show in directory</span>
                </label>
              </div>
              <input
                type="url"
                id="website"
                name="website"
                value={formData.website}
                onChange={handleInputChange}
                className="w-full px-3 sm:px-4 py-2.5 md:py-3 border-2 border-gray-200 bg-gray-50 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all text-sm md:text-base"
                placeholder="https://yourwebsite.com"
              />
            </div>
          </motion.div>
        )

      case 4:
        return (
          <motion.div
            key="step4"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 md:mb-6">
              Tell Us About Yourself
            </h2>

            {/* Profile Picture */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Profile Picture
                </label>
                <div className="flex items-start gap-4">
                  {/* Preview */}
                  <div className="flex-shrink-0">
                    {profilePicturePreview ? (
                      <div className="relative">
                        <img
                          src={profilePicturePreview}
                          alt="Profile preview"
                          className="w-24 h-24 md:w-32 md:h-32 rounded-full object-cover border-2 border-gray-200"
                        />
                        <button
                          type="button"
                          onClick={handleRemovePicture}
                          className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                          aria-label="Remove picture"
                        >
                          ×
                        </button>
                      </div>
                    ) : (
                      <div className="w-24 h-24 md:w-32 md:h-32 rounded-full bg-gray-100 border-2 border-dashed border-gray-300 flex items-center justify-center">
                        <span className="text-3xl md:text-4xl text-gray-400">👤</span>
                      </div>
                    )}
                  </div>
                  {/* Upload button */}
                  <div className="flex-1">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                      onChange={handlePictureChange}
                      className="hidden"
                      id="profilePicture"
                    />
                    <label
                      htmlFor="profilePicture"
                      className="inline-flex items-center px-4 py-2 border-2 border-gray-200 rounded-xl text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      {profilePicturePreview ? 'Change Picture' : 'Upload Picture'}
                    </label>
                    <p className="mt-2 text-xs text-gray-500">
                      JPEG, PNG, GIF or WebP. Max 5MB.
                    </p>
                    {profilePictureError && (
                      <p className="mt-2 text-sm text-red-600">
                        {profilePictureError}
                      </p>
                    )}
                  </div>
                </div>
              </div>

            {/* Bio */}
            <div>
              <label htmlFor="bio" className="block text-sm font-medium text-gray-700 mb-2">
                Bio
              </label>
              <textarea
                id="bio"
                name="bio"
                value={formData.bio}
                onChange={handleInputChange}
                rows={6}
                className="w-full px-3 sm:px-4 py-2.5 md:py-3 border-2 border-gray-200 bg-gray-50 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all resize-none text-sm md:text-base"
                placeholder="Tell us about yourself and your expertise..."
              />
            </div>
          </motion.div>
        )

      case 5:
        return (
          <motion.div
            key="step5"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3 md:mb-4">
                Select Your Tags
                </h2>
                <p className="text-sm md:text-base text-gray-600 mb-4 md:mb-6">
                  Choose tags that best describe your expertise and offerings.
                </p>

                {/* Search Input */}
                <div className="mb-4 md:mb-6">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search tags..."
                    className="w-full px-3 sm:px-4 py-2.5 md:py-3 border-2 border-gray-200 bg-gray-50 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all text-sm md:text-base"
                  />
                </div>

                {/* Filter tags based on search query */}
                {(() => {
                  const filteredTags = availableTags.filter((tag) =>
                    tag.name.toLowerCase().includes(searchQuery.toLowerCase())
                  )
                  const primaryTags = filteredTags.filter(
                    (tag) => tag.tagType === 'primary'
                  )
                  const secondaryTags = filteredTags.filter(
                    (tag) => tag.tagType === 'secondary' || !tag.tagType
                  )

                  return (
                    <>
                      {/* Primary Tags Section */}
                      {primaryTags.length > 0 && (
                        <div className="mb-4 md:mb-6">
                          <h3 className="text-base md:text-lg font-semibold text-gray-800 mb-2 md:mb-3">
                            Primary Tags
                          </h3>
                          <div className="flex flex-wrap gap-2">
                            {primaryTags.map((tag) => (
                              <motion.button
                                key={tag.id}
                                type="button"
                                onClick={() => handleTagToggle(tag.id)}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                className={`px-3 py-1.5 md:px-4 md:py-2 rounded-full text-xs md:text-sm font-medium transition-all touch-manipulation ${
                                  formData.tags.includes(tag.id)
                                    ? 'bg-primary-600 text-white shadow-md'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
                                }`}
                              >
                                {tag.name}
                              </motion.button>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Secondary Tags Section */}
                      {secondaryTags.length > 0 && (
                        <div className="mb-4 md:mb-6">
                          <h3 className="text-base md:text-lg font-semibold text-gray-800 mb-2 md:mb-3">
                            Secondary Tags
                          </h3>
                          <div className="flex flex-wrap gap-2">
                            {secondaryTags.map((tag) => (
                              <motion.button
                                key={tag.id}
                                type="button"
                                onClick={() => handleTagToggle(tag.id)}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                className={`px-3 py-1.5 md:px-4 md:py-2 rounded-full text-xs md:text-sm font-medium transition-all touch-manipulation ${
                                  formData.tags.includes(tag.id)
                                    ? 'bg-primary-600 text-white shadow-md'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
                                }`}
                              >
                                {tag.name}
                              </motion.button>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* No tags found message */}
                      {filteredTags.length === 0 && searchQuery && (
                        <p className="text-gray-500 text-sm mt-2">
                          No tags found matching &quot;{searchQuery}&quot;
                        </p>
                      )}

                      {filteredTags.length === 0 && !searchQuery && (
                        <p className="text-gray-500 text-sm mt-2">
                          No tags available
                        </p>
                      )}
                    </>
                  )
                })()}

                {/* Add New Tag Section - Only show in edit mode (requires auth) */}
                {isEditMode && (
                  <div className="mt-6 md:mt-8 pt-4 md:pt-6 border-t border-gray-200">
                    <h3 className="text-base md:text-lg font-semibold text-gray-800 mb-2 md:mb-3">
                      Add New Tag
                    </h3>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <input
                        type="text"
                        value={newTagName}
                        onChange={(e) => setNewTagName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault()
                            handleCreateTag()
                          }
                        }}
                        placeholder="Enter tag name..."
                        className="flex-1 px-4 py-2.5 md:py-3 border-2 border-gray-200 bg-gray-50 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all text-sm md:text-base"
                      />
                      <Button
                        type="button"
                        onClick={handleCreateTag}
                        variant="primary"
                        disabled={!newTagName.trim() || creatingTag}
                        className="px-4 py-2.5 md:px-6 md:py-3 text-sm md:text-base whitespace-nowrap"
                      >
                        {creatingTag ? 'Adding...' : 'Add Tag'}
                      </Button>
                    </div>
                  </div>
                )}
            </>
          </motion.div>
        )

      case 6:
        return (
          <motion.div
            key="step6"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            {isSuccess ? (
              <div className="text-center py-6 md:py-8">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', duration: 0.5 }}
                  className="text-5xl md:text-6xl mb-3 md:mb-4"
                >
                  ✅
                </motion.div>
                <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3 md:mb-4">
                  {isEditMode ? 'Profile Updated!' : 'Welcome to Wellness Loop!'}
                </h2>
                <p className="text-sm md:text-base text-gray-600 mb-4 md:mb-6">
                  {isEditMode
                    ? 'Your profile has been successfully updated.'
                    : 'Your facilitator profile has been created successfully.'}
                </p>
                <Button
                  onClick={handleGoToDashboard}
                  variant="primary"
                  className="px-6 md:px-8 py-2.5 md:py-3 text-sm md:text-base"
                >
                  {isEditMode ? 'Go to Dashboard' : 'Get Started'}
                </Button>
              </div>
            ) : (
              <>
                <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3 md:mb-4">
                  Certifications
                </h2>
                <p className="text-sm md:text-base text-gray-600 mb-4 md:mb-6">
                  Add your certifications and credentials (optional). These will be reviewed by the Wellness Loop team.
                </p>

                {/* Existing certifications list (edit mode) or pending certs (register mode) */}
                {(() => {
                  const certList = isEditMode ? certifications : pendingCerts
                  return certList.length > 0 && (
                    <div className="space-y-3 mb-6">
                      {certList.map((cert, index) => (
                        <div
                          key={cert.id || `pending-${index}`}
                          className="flex items-center justify-between gap-3 p-3 border-2 border-gray-200 rounded-xl bg-gray-50"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-900 truncate">{cert.name}</div>
                            <div className="text-xs text-gray-500 truncate">{cert.issuer}</div>
                            {isEditMode && cert.verificationStatus && (
                              <div className="mt-1">
                                {cert.verificationStatus === 'verified' && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Verified</span>
                                )}
                                {cert.verificationStatus === 'pending' && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">Pending Review</span>
                                )}
                                {cert.verificationStatus === 'rejected' && (
                                  <>
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">Rejected</span>
                                    {cert.adminNotes && (
                                      <p className="mt-1 text-xs text-red-600 bg-red-50 rounded-lg px-2 py-1">{cert.adminNotes}</p>
                                    )}
                                  </>
                                )}
                              </div>
                            )}
                            {!isEditMode && cert.file && (
                              <div className="text-xs text-primary-600 mt-1">{cert.file.name}</div>
                            )}
                            {isEditMode && cert.documentOriginalName && (
                              <div className="text-xs text-primary-600 mt-1">{cert.documentOriginalName}</div>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={async () => {
                              if (isEditMode) {
                                try {
                                  await api.delete(`/api/facilitators/certifications/${cert.id}`)
                                  setCertifications((prev) => prev.filter((c) => c.id !== cert.id))
                                } catch (err) {
                                  console.error('Error deleting cert:', err)
                                  setError('Failed to delete certification')
                                }
                              } else {
                                setPendingCerts((prev) => prev.filter((_, i) => i !== index))
                              }
                            }}
                            className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-red-100 text-red-600 hover:bg-red-200 transition-colors"
                            aria-label="Remove certification"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )
                })()}

                {/* Add Certification Form */}
                <div className="border-2 border-dashed border-gray-300 rounded-xl p-4 md:p-6">
                  <h3 className="text-base font-semibold text-gray-800 mb-3">
                    Add Certification
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Certification Name *
                      </label>
                      <input
                        type="text"
                        value={certFormData.name}
                        onChange={(e) => setCertFormData((prev) => ({ ...prev, name: e.target.value }))}
                        className="w-full px-3 sm:px-4 py-2.5 border-2 border-gray-200 bg-gray-50 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all text-sm"
                        placeholder="e.g., RYT-500 Yoga Alliance"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Issuer *
                      </label>
                      <input
                        type="text"
                        value={certFormData.issuer}
                        onChange={(e) => setCertFormData((prev) => ({ ...prev, issuer: e.target.value }))}
                        className="w-full px-3 sm:px-4 py-2.5 border-2 border-gray-200 bg-gray-50 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all text-sm"
                        placeholder="e.g., Yoga Alliance International"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Supporting Document (optional)
                      </label>
                      <input
                        ref={certFileInputRef}
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) => setCertDocumentFile(e.target.files[0] || null)}
                        className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                      />
                      <p className="mt-1 text-xs text-gray-500">PDF, JPEG, or PNG. Max 5MB.</p>
                    </div>
                    <Button
                      type="button"
                      onClick={async () => {
                        if (!certFormData.name.trim() || !certFormData.issuer.trim()) return

                        if (isEditMode) {
                          try {
                            setSavingCert(true)
                            const submitData = new FormData()
                            submitData.append('name', certFormData.name.trim())
                            submitData.append('issuer', certFormData.issuer.trim())
                            if (certDocumentFile) {
                              submitData.append('document', certDocumentFile)
                            }
                            const response = await api.post('/api/facilitators/certifications', submitData, {
                              headers: { 'Content-Type': 'multipart/form-data' },
                            })
                            setCertifications((prev) => [response.data.data, ...prev])
                            setCertFormData({ name: '', issuer: '' })
                            setCertDocumentFile(null)
                            if (certFileInputRef.current) certFileInputRef.current.value = ''
                          } catch (err) {
                            console.error('Error creating cert:', err)
                            setError('Failed to add certification')
                          } finally {
                            setSavingCert(false)
                          }
                        } else {
                          setPendingCerts((prev) => [
                            ...prev,
                            {
                              name: certFormData.name.trim(),
                              issuer: certFormData.issuer.trim(),
                              file: certDocumentFile || null,
                            },
                          ])
                          setCertFormData({ name: '', issuer: '' })
                          setCertDocumentFile(null)
                          if (certFileInputRef.current) certFileInputRef.current.value = ''
                        }
                      }}
                      variant="secondary"
                      disabled={!certFormData.name.trim() || !certFormData.issuer.trim() || savingCert}
                      className="px-4 py-2 text-sm"
                    >
                      {savingCert ? 'Adding...' : 'Add Certification'}
                    </Button>
                  </div>
                </div>
              </>
            )}
          </motion.div>
        )

      default:
        return null
    }
  }

  if (loading || verifying) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-purple-50 to-pink-50 flex items-center justify-center px-4">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-sm md:text-base text-gray-600">
            {isEditMode ? 'Loading your profile...' : 'Loading...'}
          </p>
        </div>
      </div>
    )
  }

  if (error && !verifying) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-purple-50 to-pink-50 flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl md:rounded-3xl shadow-2xl p-6 sm:p-8 md:p-12 max-w-md w-full text-center"
        >
          <div className="text-5xl md:text-6xl mb-3 md:mb-4">⚠️</div>
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3 md:mb-4">Error</h2>
          <p className="text-sm md:text-base text-gray-600 mb-4 md:mb-6">{error}</p>
          <Button onClick={() => navigate('/')} variant="primary" className="px-4 py-2 md:px-6 md:py-3 text-sm md:text-base">
            Go to Home
          </Button>
        </motion.div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-purple-50 to-pink-50 py-4 md:py-8 px-3 sm:px-4">
      <div className="max-w-4xl mx-auto">
        {/* Logo */}
        <div className="mb-4 md:mb-6 flex justify-center">
          <img src="/logo.png" alt="Wellness Loop Logo" className="h-12 md:h-16 w-auto" />
        </div>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-4 md:mb-8"
        >
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {/* Complete Your <span className="text-primary-600">Profile</span> */}
          </h1>
        </motion.div>

        {/* Progress Bar */}
        {renderProgressBar()}

        {/* Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl md:rounded-3xl shadow-2xl p-4 sm:p-6 md:p-8 lg:p-12"
        >
          <form onSubmit={(e) => {
            e.preventDefault()
            if (currentStep === 6) {
              handleSubmit(e)
            }
          }} onKeyDown={(e) => {
            // Prevent form submission on Enter key unless explicitly clicking submit button
            if (e.key === 'Enter') {
              if (currentStep === 6) {
                if (saving || isSuccess || e.target.type !== 'submit') {
                  e.preventDefault()
                }
              } else {
                e.preventDefault()
              }
            }
          }}>
            {/* Error Message */}
            {submitError && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="bg-red-50 border border-red-200 text-red-700 px-3 sm:px-4 py-2.5 md:py-3 rounded-lg mb-4 md:mb-6 text-sm md:text-base"
              >
                {submitError}
              </motion.div>
            )}

            {/* Step Content */}
            <AnimatePresence mode="wait">
              {renderStepContent()}
            </AnimatePresence>

            {/* Navigation Buttons */}
            <div className="flex justify-between items-center gap-3 mt-6 md:mt-8 pt-4 md:pt-6 border-t border-gray-200">
              <div className="flex-shrink-0">
                {currentStep > 1 && !isSuccess && (
                  <Button
                    type="button"
                    onClick={handlePrevious}
                    variant="secondary"
                    className="px-4 py-2 md:px-6 md:py-3 text-sm md:text-base"
                  >
                    Previous
                  </Button>
                )}
              </div>
              <div className="flex-shrink-0">
                {!isSuccess && (
                  currentStep === 6 ? (
                    <Button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        handleSubmit(e)
                      }}
                      variant="primary"
                      disabled={saving}
                      className="px-4 py-2 md:px-6 md:py-3 text-sm md:text-base whitespace-nowrap"
                    >
                      {saving ? 'Saving...' : isEditMode ? 'Save Profile' : 'Register'}
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      onClick={handleNext}
                      variant="primary"
                      className="px-4 py-2 md:px-6 md:py-3 text-sm md:text-base"
                    >
                      Next
                    </Button>
                  )
                )}
              </div>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  )
}

export default FacilitatorOnboarding
